import { TEST_IDS } from '@va/constants';
import { ExternalLinkIcon, Spinner } from '@va/icons';
import { useTranslate } from '@va/localization';
import { CustomDomainPlatformType, CustomDomainsApi, CustomDomainType } from '@va/shared/api/custom-domains';
import { useWebsiteTrackingCodeDomainProvisioningSteps } from '@va/shared/custom-domains';
import { Button, fontWeights, InputMessage, Paragraph, TextInput } from '@va/ui/design-system';
import { getWellFormedUrl } from '@va/util/helpers';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { CustomDomainActions, DomainsList } from '../shared/DomainsList';
import { useCustomDomainSaveButtonState, useRefreshPendingDomain } from '../shared/reusables';
import { useWebsiteDomainsList } from '../shared/useWebsiteDomainsList';
import { getCustomDomainValidationSchema } from '../shared/validation-schema';

export const CustomWebsiteDomainForm = ({
  websiteId,
  platform,
  domainType,
  onError,
  afterSave,
  actions,
  onActiveDomainChange,
  editEnabled,
  overrideInputValue,
  inputTooltip,
}: {
  websiteId: string;
  platform: CustomDomainPlatformType;
  domainType: CustomDomainType.trackingCode;
  onError: (err: string) => void;
  afterSave: (args: { domain: string; customDomainId: number; websiteId: string; resetForm: () => void }) => void;
  actions: CustomDomainActions;
  onActiveDomainChange?: () => void;
  editEnabled: boolean;
  overrideInputValue?: string;
  inputTooltip?: string;
}) => {
  const [isLoadingCustomDomainCreation, setIsLoadingCustomDomainCreation] = useState(false);
  const translate = useTranslate();

  const {
    activeDomain,
    pendingDomain,
    inactiveDomains,
    isLoading: isLoadingCustomDomainsList,
    mutate: mutateCustomDomainsList,
  } = useWebsiteDomainsList(websiteId, domainType);

  const { handleChange, values, errors, setFieldValue, handleBlur, touched, dirty, resetForm, submitForm } = useFormik({
    initialValues: {
      customDomain: '',
    },
    onSubmit: async ({ customDomain }) => {
      if (!websiteId) return;
      try {
        setIsLoadingCustomDomainCreation(true);
        const res = await CustomDomainsApi.createWebsiteDomain({
          websiteId: websiteId,
          domain: customDomain,
          platform: platform,
          type: domainType,
          skipActivation: true,
        });
        await mutateCustomDomainsList();
        resetForm();

        afterSave({
          customDomainId: res.payload.id,
          domain: values.customDomain,
          websiteId: websiteId,
          resetForm: resetForm,
        });
      } catch (error) {
        console.error(error);
        onError(CustomDomainsApi.extractApiErrorMessage(error, translate));
      } finally {
        setIsLoadingCustomDomainCreation(false);
      }
    },
    enableReinitialize: true,
    validationSchema: getCustomDomainValidationSchema(translate),
  });

  const { isSaveButtonDisabled, saveButtonTooltip } = useCustomDomainSaveButtonState({
    pendingDomain,
    dirty,
    isLoading: isLoadingCustomDomainsList,
  });

  useRefreshPendingDomain({ pendingDomain: pendingDomain, mutate: mutateCustomDomainsList });

  const { steps } = useWebsiteTrackingCodeDomainProvisioningSteps();

  useEffect(() => {
    onActiveDomainChange?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDomain]);

  const customDomainValue = overrideInputValue || values.customDomain;

  return (
    <div>
      <div className='space-y-3'>
        <DomainsList
          actions={actions}
          activeDomain={activeDomain}
          pendingDomain={pendingDomain}
          inactiveDomains={inactiveDomains}
          steps={steps}
        />

        <div className='w-full flex gap-3 items-center bg-white-snow rounded-18 text-gray-devil py-6px px-3 overflow-x-auto scrollbar scrollbar-thumb'>
          {isLoadingCustomDomainsList ? (
            <Skeleton className='h-40 rounded-18' />
          ) : (
            <>
              <div>
                <Paragraph colorClassName='text-gray-devil' weight={fontWeights.medium}>
                  https://
                </Paragraph>
              </div>
              <TextInput
                tooltip={inputTooltip}
                disabled={!editEnabled}
                wrapperClassName='overflow-hidden grow'
                placeholder={translate('standalone.customDomains.trackingCode.input.placeholder')}
                label={translate('standalone.customDomains.trackingCode.input.label')}
                name='customDomain'
                value={customDomainValue}
                clearField={() => setFieldValue('customDomain', '')}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                error={(touched.customDomain && errors.customDomain) || undefined}
                showErrorMessage={false}
                bgColor='#F0F0F0'
                bgColorHover='#F9F9F9'
              />

              <Button
                onClick={() => {
                  window.open(getWellFormedUrl(customDomainValue));
                }}
                disabled={!customDomainValue}
                color='quaternary'
                icon={() => <ExternalLinkIcon color='#3C3C3C' />}
                data-testid={TEST_IDS.generic.buttons.goToWebsite}
              />
            </>
          )}
        </div>

        {touched.customDomain && errors.customDomain && <InputMessage error={errors.customDomain} />}

        {editEnabled && (
          <Button
            disabled={isSaveButtonDisabled}
            tooltip={saveButtonTooltip}
            tooltipProps={{ maxWidth: 'max-w-[25rem]' }}
            text={translate('button.saveChanges')}
            className='w-full justify-center'
            icon={isLoadingCustomDomainCreation ? () => <Spinner /> : undefined}
            onClick={() => {
              submitForm();
            }}
          />
        )}
      </div>
    </div>
  );
};
